/**
 * Shared utilities for converting between plan data and table display formats
 * This file consolidates conversion logic used by both 1-line and 3-line layouts
 */

import { enumToLabel, getPlacingMethodDisplayText } from '../services/excel-table.service';
import { formatDateToYYMMDD } from '../components/materials/datepickers/datepicker-in-excel';

import type { CreatePlanRequest } from '../types/plan.types';
import type { PlanTableRow } from '../components/common/handsontable.types';
import type { SearchOption } from '@/types/search';

/**
 * Column indices for 1-line table format
 * Maps field names to their column positions (0-indexed)
 */
export const ONE_LINE_COLUMN_INDEX = {
  ORDER_STATUS: 0,
  CUSTOMER_SUPPLIER_ID: 1,
  PLACEMENT_DATE: 2,
  TIME_HOPE: 3,
  TIME_POSSIBLE: 4,
  TIME_POSSIBLE_REMARKS: 5,
  PLACE_TO_PLACE: 6,
  QUANTITY: 7,
  QUANTITY_SUFFIX: 8,
  QUANTITY_UNIT: 9,
  FORMULATION_PRODUCT_TYPE: 10,
  FORMULATION_PRODUCT: 11,
  VEHICLE_SIZE: 12,
  PRECEDING_MATERIAL_TYPE: 13,
  PRECEDING_MATERIAL_QUANTITY: 14,
  PITCH_HOPE: 15,
  PITCH_POSSIBLE: 16,
  PLACING_METHOD: 17,
  WITNESS_COMPANY_ID: 18,
  WITNESS_NAME: 19,
  WITNESS_TEL: 20,
  REMARKS: 21,
  REMARKS_FOR_CLIENT: 22,
} as const;

/**
 * Get company name by ID from options
 * SearchOption format: { key: id, value: name, label: name }
 */
export const getCompanyNameById = (
  id: string | null | undefined,
  options: SearchOption[]
): string => {
  if (!id) return '';
  const option = options.find((opt) => opt.key === String(id));
  return option ? String(option.label ?? '') : '';
};

/**
 * Convert a plan field value to its display format
 * Handles enum conversions, company lookups, and date formatting
 */
export const convertFieldToDisplay = (
  fieldKey: keyof typeof ONE_LINE_COLUMN_INDEX,
  value: unknown,
  plantOptions: SearchOption[] = [],
  witnessCompanyOptions: SearchOption[] = []
): unknown => {
  switch (fieldKey) {
    case 'ORDER_STATUS':
      return enumToLabel.orderStatus(value as number | null | undefined);
    case 'CUSTOMER_SUPPLIER_ID':
      return getCompanyNameById(value as string | null | undefined, plantOptions);
    case 'PLACEMENT_DATE':
      return formatDateToYYMMDD(value as string);
    case 'QUANTITY':
      // Display empty string instead of 0
      return value === 0 || value === null || value === undefined ? '' : value;
    case 'QUANTITY_SUFFIX':
      return enumToLabel.quantitySuffix((value as number | null) ?? null);
    case 'FORMULATION_PRODUCT_TYPE':
      return enumToLabel.formulationProductType(value as number | null | undefined);
    case 'VEHICLE_SIZE':
      return enumToLabel.vehicleSize(value as number | null | undefined);
    case 'PRECEDING_MATERIAL_TYPE':
      return enumToLabel.materialType((value as number | null) ?? null);
    case 'PLACING_METHOD':
      // Handle null, undefined, or empty string
      if (value === null || value === undefined || value === '') {
        return '';
      }
      // If value is already a string label, preserve it; otherwise convert number to label
      return typeof value === 'string' ? value : enumToLabel.placingMethod(value as number);
    case 'WITNESS_COMPANY_ID':
      return getCompanyNameById(value as string | null | undefined, witnessCompanyOptions);
    // For all other fields, return as-is with null coalescing
    default:
      return value ?? '';
  }
};

/**
 * Convert plan object to 1-line table row format
 * This is the core conversion logic extracted from getDefaultPlanData
 */
export const planToTableRow = (
  plan: CreatePlanRequest,
  plantOptions: SearchOption[] = [],
  witnessCompanyOptions: SearchOption[] = [],
  canUpdate: boolean = true
): PlanTableRow => {
  const customerSupplierLookupId = plan.customerSupplierId ?? plan.customerSupplier?.id;
  const witnessCompanyLookupId = plan.witnessCompanyId ?? plan.witnessCompany?.id;
  const getCustomerSupplierName = getCompanyNameById(customerSupplierLookupId, plantOptions);
  const getWitnessCompanyName = getCompanyNameById(witnessCompanyLookupId, witnessCompanyOptions);

  const placingMethodDisplay = getPlacingMethodDisplayText(
    plan.placingMethod,
    plan.placingMethodOthers
  );

  return [
    enumToLabel.orderStatus(plan.orderStatus),
    canUpdate && getCustomerSupplierName
      ? getCustomerSupplierName
      : (plan.customerSupplier?.abbreviationName ?? ''),
    formatDateToYYMMDD(plan.placementDate),
    plan.timeHope || '',
    plan.timePossible,
    plan.timePossibleRemarks || '',
    plan.placeToPlace,
    plan.quantity === null || plan.quantity === undefined ? '' : plan.quantity,
    enumToLabel.quantitySuffix(plan.quantitySuffix ?? null),
    plan.quantityUnit || '',
    enumToLabel.formulationProductType(plan.formulationProductType),
    plan.formulationProduct,
    enumToLabel.vehicleSize(plan.vehicleSize),
    enumToLabel.materialType(plan.precedingMaterialType ?? null),
    plan.precedingMaterialQuantity || '',
    plan.pitchHope || '',
    plan.pitchPossible || '',
    placingMethodDisplay,
    canUpdate && getWitnessCompanyName ? getWitnessCompanyName : (plan.witnessCompany?.name ?? ''),
    plan.witnessName,
    plan.witnessTel,
    plan.remarks || '',
    plan.remarksForClient || '',
  ];
};

/**
 * Split 1-line table row into 3-line format
 * Returns [line1, line2, line3] arrays based on column mappings
 */
export const splitRowInto3Lines = (
  rowData: unknown[],
  sttNumber: number
): { line1: unknown[]; line2: unknown[]; line3: unknown[] } => {
  const line1 = [
    sttNumber,
    rowData[ONE_LINE_COLUMN_INDEX.ORDER_STATUS],
    rowData[ONE_LINE_COLUMN_INDEX.CUSTOMER_SUPPLIER_ID],
    rowData[ONE_LINE_COLUMN_INDEX.PLACEMENT_DATE],
    rowData[ONE_LINE_COLUMN_INDEX.TIME_HOPE],
    rowData[ONE_LINE_COLUMN_INDEX.TIME_POSSIBLE],
    rowData[ONE_LINE_COLUMN_INDEX.TIME_POSSIBLE_REMARKS],
    rowData[ONE_LINE_COLUMN_INDEX.PLACE_TO_PLACE],
    rowData[ONE_LINE_COLUMN_INDEX.QUANTITY],
    rowData[ONE_LINE_COLUMN_INDEX.QUANTITY_SUFFIX],
    rowData[ONE_LINE_COLUMN_INDEX.QUANTITY_UNIT],
    rowData[ONE_LINE_COLUMN_INDEX.FORMULATION_PRODUCT_TYPE],
    rowData[ONE_LINE_COLUMN_INDEX.FORMULATION_PRODUCT],
  ];

  // Line2 structure matches ensureWrappedRowColumnTableLength:
  // [0: vehicleSize, 1: precedingMaterialType, 2: precedingMaterialQuantity, 3: pitchHope,
  //  4: undefined, 5: pitchPossible, 6: placingMethod, 7: witnessCompanyId,
  //  8: undefined, 9: witnessName, 10: undefined, 11: witnessTel]
  const line2 = [
    '',
    rowData[ONE_LINE_COLUMN_INDEX.VEHICLE_SIZE] ?? '', // index 1
    rowData[ONE_LINE_COLUMN_INDEX.PRECEDING_MATERIAL_TYPE] ?? '', // index 2
    rowData[ONE_LINE_COLUMN_INDEX.PRECEDING_MATERIAL_QUANTITY] ?? '', // index 3
    rowData[ONE_LINE_COLUMN_INDEX.PITCH_HOPE] ?? '', // index 4
    undefined, // index 5 - placeholder
    rowData[ONE_LINE_COLUMN_INDEX.PITCH_POSSIBLE] ?? '', // index 6
    rowData[ONE_LINE_COLUMN_INDEX.PLACING_METHOD] ?? '', // index 7
    rowData[ONE_LINE_COLUMN_INDEX.WITNESS_COMPANY_ID] ?? '', // index 8
    undefined, // index 9 - placeholder
    rowData[ONE_LINE_COLUMN_INDEX.WITNESS_NAME] ?? '', // index 10
    undefined, // index 11 - placeholder
    rowData[ONE_LINE_COLUMN_INDEX.WITNESS_TEL] ?? '', // index 12
  ];

  const line3 = [
    '',
    rowData[ONE_LINE_COLUMN_INDEX.REMARKS],
    '',
    '',
    '',
    '',
    '',
    rowData[ONE_LINE_COLUMN_INDEX.REMARKS_FOR_CLIENT],
    '',
    '',
    '',
    '',
    '',
  ];

  return { line1, line2, line3 };
};

/**
 * Merge 3-line format back to 1-line format
 * Inverse operation of splitRowInto3Lines
 * Line2 structure: [0: '', 1: vehicleSize, 2: precedingMaterialType, 3: precedingMaterialQuantity,
 *  4: pitchHope, 5: undefined, 6: pitchPossible, 7: placingMethod, 8: witnessCompanyId,
 *  9: undefined, 10: witnessName, 11: undefined, 12: witnessTel]
 */
export const merge3LinesIntoRow = (
  line1: unknown[],
  line2: unknown[],
  line3: unknown[]
): PlanTableRow => {
  return [
    line1[1] ?? '', // ORDER_STATUS (skip STT at index 0)
    line1[2] ?? '', // CUSTOMER_SUPPLIER_ID
    line1[3] ?? '', // PLACEMENT_DATE
    line1[4] ?? '', // TIME_HOPE
    line1[5] ?? '', // TIME_POSSIBLE
    line1[6] ?? '', // TIME_POSSIBLE_REMARKS
    line1[7] ?? '', // PLACE_TO_PLACE
    line1[8] ?? '', // QUANTITY
    line1[9] ?? '', // QUANTITY_SUFFIX
    line1[10] ?? '', // QUANTITY_UNIT
    line1[11] ?? '', // FORMULATION_PRODUCT_TYPE
    line1[12] ?? '', // FORMULATION_PRODUCT
    line2[1] ?? '', // VEHICLE_SIZE
    line2[2] ?? '', // PRECEDING_MATERIAL_TYPE
    line2[3] ?? '', // PRECEDING_MATERIAL_QUANTITY
    line2[4] ?? '', // PITCH_HOPE
    line2[6] ?? '', // PITCH_POSSIBLE (skip undefined at index 5)
    line2[7] ?? '', // PLACING_METHOD
    line2[8] ?? '', // WITNESS_COMPANY_ID
    line2[10] ?? '', // WITNESS_NAME (skip undefined at index 9)
    line2[12] ?? '', // WITNESS_TEL (skip undefined at index 11)
    line3[1] ?? '', // REMARKS (備考 社内)
    line3[7] ?? '', // REMARKS_FOR_CLIENT (備考 ゼネコン)
  ];
};
